import React, { useEffect, useContext, useCallback } from 'react';
import { ENGINE_ATTRIBUTES_QUERY } from '../../queries';
import { ENGINE_ATTRIBUTE_DELETED } from '../../subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import { useMutation } from 'hooks/apollo/useMutation';
import { ENGINE_ATTRIBUTE_DELETE } from 'features/engineAttributes/mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import EngineAttributeItem from './EngineAttributeItem';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const engineAttributesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'EngineAttributes.Name',
      type: 'text'
    },
    {
      name: 'description',
      caption: 'EngineAttributes.Description',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'EngineAttributes.Name',
      value: 'AttributeName'
    },
    {
      caption: 'EngineAttributes.Description',
      value: 'Description'
    }
  ],
  pager: {
    orderBy: 'AttributeName',
    direction: 'asc'
  }
});

const EngineAttributeListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(engineAttributesStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: ENGINE_ATTRIBUTES_QUERY,
        name: 'engineAttributes'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(ENGINE_ATTRIBUTE_DELETED, {
    onData: ({ data }) => {
      addToast(t('EngineAttributes.EngineAttributeDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'engineAttributes' });
      client.cache.evict({
        id: `EngineAttribute:{"engineAttributeId":${data.data.engineAttributeDeleted.engineAttributeId}}`
      });
      client.cache.gc();
    }
  });

  const [deleteEngineAttribute] = useMutation(ENGINE_ATTRIBUTE_DELETE);

  const handleDelete = useCallback(
    engineAttributeId => {
      confirm(
        () => {
          deleteEngineAttribute({ variables: { engineAttributeId: engineAttributeId } });
        },
        {
          content: t('EngineAttributes.DeleteEngineAttribute')
        }
      );
    },
    [deleteEngineAttribute, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'engineAttributes' });
      client.cache.gc();
    }
  }, [activeService, client]);

  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions, t]);

  const itemComponent = useCallback(({ data }) => <EngineAttributeItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'EngineAttributes.OrderNo', width: '100px' },
      { name: 'name', caption: 'EngineAttributes.Name' },
      { name: 'description', caption: 'EngineAttributes.Description' },
      { name: 'expression', caption: 'EngineAttributes.Expression', width: 40 },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='engine-attributes-browser' store={engineAttributesStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={engineAttributesStore} options={fetchingOptions} />
    </Card>
  );
};

export default EngineAttributeListBrowser;
